import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSliderModule } from '@angular/material/slider';
import { FormsModule } from '@angular/forms';
import { ImageCropperComponent, ImageCroppedEvent, LoadedImage } from 'ngx-image-cropper';

interface CropDialogData {
  imageFile: File;
}

@Component({
  selector: 'app-image-crop-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatSliderModule,
    ImageCropperComponent
  ],
  template: `
    <div class="crop-dialog">
      <div class="crop-header">
        <h2>Crop Event Image</h2>
        <button mat-icon-button (click)="cancel()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="crop-body">
        <image-cropper
          [imageFile]="data.imageFile"
          [maintainAspectRatio]="true"
          [aspectRatio]="1200 / 630"
          [resizeToWidth]="1200"
          format="jpeg"
          [imageQuality]="90"
          (imageCropped)="onImageCropped($event)"
          (imageLoaded)="onImageLoaded()"
          (loadImageFailed)="onLoadFailed()">
        </image-cropper>

        <p class="crop-hint" *ngIf="imageLoaded">
          Drag to reposition, use the handles to resize. The final image will be 1200×630.
        </p>

        <p class="crop-error" *ngIf="loadFailed">
          Couldn't load this image. Please choose a different file.
        </p>
      </div>

      <div class="crop-actions">
        <button mat-button (click)="cancel()">Cancel</button>
        <button mat-raised-button color="primary" [disabled]="!croppedBlob" (click)="confirm()">
          <mat-icon>check</mat-icon>
          Use This Crop
        </button>
      </div>
    </div>
  `,
  styles: [`
    .crop-dialog {
      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: 700px;
    }
    .crop-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1rem 1.5rem;
      border-bottom: 1px solid #e5e7eb;

      h2 { margin: 0; font-size: 1.25rem; }
    }
    .crop-body {
      padding: 1.5rem;
      min-height: 400px;
    }
    image-cropper {
      max-height: 450px;
    }
    .crop-hint {
      margin-top: 1rem;
      font-size: 0.85rem;
      color: #6b7280;
      text-align: center;
    }
    .crop-error {
      color: #dc2626;
      text-align: center;
    }
    .crop-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      padding: 1rem 1.5rem;
      border-top: 1px solid #e5e7eb;
    }
  `]
})
export class ImageCropDialogComponent {
  croppedBlob: Blob | null = null;
  imageLoaded = false;
  loadFailed = false;

  constructor(
    public dialogRef: MatDialogRef<ImageCropDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CropDialogData
  ) {}

  onImageCropped(event: ImageCroppedEvent): void {
    if (event.blob) {
      this.croppedBlob = event.blob;
    }
  }

  onImageLoaded(): void {
    this.imageLoaded = true;
  }

  onLoadFailed(): void {
    this.loadFailed = true;
  }

  confirm(): void {
    if (!this.croppedBlob) return;
    // Convert blob back to a File so the existing upload service (which expects a File) still works unchanged
    const file = new File([this.croppedBlob], 'event-image.jpg', { type: 'image/jpeg' });
    this.dialogRef.close({ success: true, file });
  }

  cancel(): void {
    this.dialogRef.close({ success: false });
  }
}
